import React from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import {
  PlayExperience,
  playExperiences,
  MATCHMAKING_ENABLED,
} from 'src/pages/dashboard/playExperiences'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ViewNavigation from '@components/common/ViewNavigation'
import { GlassOutlineButton } from '@components/common/Glass'
import {
  mainColorLighter,
  altColorLighter,
  navy,
  headingFont,
  displayFont,
  shadow1,
} from 'src/ui/theme/tokens'

type ModeSelectProps = {
  experience?: PlayExperience
  onSelectExperience: (experience: PlayExperience) => void
  onOpenHowToPlay: () => void
}
export default function ModeSelect({
  experience,
  onSelectExperience,
  onOpenHowToPlay,
}: ModeSelectProps): JSX.Element {
  const ids: PlayExperience[] = [
    'solo',
    'host',
    'local',
    'lobby',
    ...(MATCHMAKING_ENABLED ? ['online' as const] : []),
  ]
  const icons = {
    solo: <PersonRoundedIcon />,
    host: <MicRoundedIcon />,
    local: <SwapHorizRoundedIcon />,
    lobby: <DevicesRoundedIcon />,
    online: <GroupsRoundedIcon />,
  }
  const cards = ids.map((id) => ({
    id,
    ...playExperiences[id],
    label: playExperiences[id].badge,
    text: playExperiences[id].description,
    icon: icons[id],
    iconColor: id === 'solo' ? navy : id === 'host' ? '#876414' : '#693413',
    color: id === 'solo' ? mainColorLighter : id === 'host' ? '#fff5cc' : altColorLighter,
    action: () => onSelectExperience(id),
  }))
  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', pb: 4 }}>
      <ViewNavigation sx={{ px: { xs: 0, sm: 1 } }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: displayFont,
            fontSize: { xs: 'clamp(18px, 5vw, 21px)', sm: 24 },
            fontWeight: 500,
            color: '#516f86',
            textAlign: 'left',
            whiteSpace: 'nowrap',
          }}
        >
          Choose your mode
        </Typography>
        <GlassOutlineButton
          variant="outlined"
          onClick={onOpenHowToPlay}
          startIcon={<InfoOutlinedIcon fontSize="inherit" sx={{ color: '#7c91a3' }} />}
          sx={{ color: '#60768b', flexShrink: 0 }}
        >
          How to Play
        </GlassOutlineButton>
      </ViewNavigation>
      <Box
        display="grid"
        gap={2}
        sx={{ gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}
      >
        {cards.map((card) => (
          <ButtonBase
            key={card.id}
            onClick={card.action}
            aria-pressed={experience === card.id}
            sx={{
              textAlign: 'left',
              gridColumn: card.id === 'online' ? '1 / -1' : undefined,
              p: { xs: 2.5, sm: 3 },
              borderRadius: '28px',
              background: '#ffffff',
              border: '1px solid #d6e4ef',
              boxShadow: shadow1,
              justifyContent: 'flex-start',
              gap: 2,
              '@media (hover:hover) and (pointer:fine)': {
                '&:hover': {
                  background: card.color,
                  transform: 'translateY(-2px)',
                },
              },
              transition: 'transform 160ms, background-color 160ms',
            }}
          >
            <Box
              sx={{
                width: 58,
                height: 64,
                borderRadius: '20px',
                background: card.color,
                color: card.iconColor,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                transform: 'rotate(-5deg)',
                '& svg': { fontSize: 32 },
              }}
            >
              {card.icon}
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography
                sx={{
                  fontSize: 10,
                  letterSpacing: '.08em',
                  fontWeight: 700,
                  color: 'text.secondary',
                }}
              >
                {card.label}
              </Typography>
              <Typography
                data-rt-display
                sx={{
                  fontFamily: headingFont,
                  fontWeight: 800,
                  fontSize: { xs: 23, sm: 28 },
                  lineHeight: 1.3,
                }}
              >
                {card.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {card.text}
              </Typography>
            </Box>
            <ArrowForwardRoundedIcon sx={{ display: { xs: 'none', sm: 'block' } }} />
          </ButtonBase>
        ))}
      </Box>
    </Box>
  )
}
